import { ComponentPropsWithoutRef, ReactElement, VFC, useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import * as Sentry from '@sentry/nextjs';
import trackUserEvent from '@/util/eventTracking';
import cn from 'classnames';
import useSWR from 'swr';

import { API, Structure } from '@/types/app';

import TableHead from '@/components/list/AssetListTableHead';
import TableRow from '@/components/list/AssetListTableRow';
import ServerError from '@/pages/500';

enum AssetListTabs {
  ALL = 'ALL',
  TRADEABLE = 'TRADEABLE',
  GAINERS = 'GAINERS',
  LOSERS = 'LOSERS',
}

/**
 * A reusable Table component for asset comparisons.
 * @component
 */
const AssetListTable: VFC<
  {
    type?: keyof typeof Structure.PagedResponseAssetInfoType;
  } & ComponentPropsWithoutRef<'div'>
> = ({ type, ...props }): ReactElement => {
  const { t } = useTranslation(['detail', 'common']);

  const [pageIndex, setPageIndex] = useState(0);
  const [query, setQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<keyof typeof AssetListTabs>(AssetListTabs.ALL);
  const [pageMax, setPageMax] = useState<number>(0);

  const { data, error } = useSWR<API.Response<Structure.PagedResponseAssetInfo>>(
    `/api/data/findAssets?query=${query}&pageSize=20&page=${pageIndex}${
      type ? `&type=${type}` : ''
    }${activeTab === AssetListTabs.GAINERS ? `&sortOrder=DESC&sortBy=changePercent24H` : ''}${
      activeTab === AssetListTabs.LOSERS ? `&sortOrder=ASC&sortBy=changePercent24H` : ''
    }${activeTab === AssetListTabs.TRADEABLE ? `&isTradingDisabled=false` : ''}`,
  );

  useEffect(() => {
    if (data) {
      setPageMax(Math.ceil(data.result.totalNumber / data.result.pageSize) - 1);
    }
  }, [pageIndex, query, activeTab, pageMax, data]);

  if (error) {
    Sentry.captureException(new Error(error?.message), {
      extra: { error },
    });

    return <ServerError button={false} />;
  }

  const tabs: {
    [key in AssetListTabs]: string;
  } = t('tabs', {
    returnObjects: true,
    ns: 'detail',
  });

  // const pageMax = Math.ceil(data.result.totalNumber / data.result.pageSize) - 1;

  const getPaginationPages = () => {
    if (pageIndex === 0) return [0, 1, 2];

    if (pageIndex === pageMax) return [pageIndex - 2, pageIndex - 1, pageIndex];

    if (pageIndex === pageMax - 1) return [pageIndex - 1, pageIndex];

    return [pageIndex - 1, pageIndex, pageIndex + 1];
  };

  return (
    <div className="grid grid-cols-1 gap-4 desktop:gap-y-6" {...props}>
      <div className="flex flex-col gap-4 desktop:flex-row desktop:items-end desktop:justify-between desktop:gap-x-0">
        <ul className="invisible-scroll flex flex-wrap items-center gap-5 tablet:gap-8">
          {Object.keys(tabs).map((item: keyof typeof AssetListTabs) => (
            <li key={item}>
              <button
                type="button"
                className={cn(
                  'leading-[26px] whitespace-nowrap border-b-2 transition duration-200',

                  item === activeTab
                    ? 'font-medium !cursor-default border-b-black-1 text-black-1'
                    : 'font-normal border-b-transparent text-gray-1 hover:text-blue-3 body-1',
                )}
                onClick={() => {
                  setActiveTab(item);
                  setPageIndex(0);
                }}
              >
                {tabs[item]}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex w-full items-center desktop:max-w-96">
          <input
            value={query}
            onChange={(e) => {
              trackUserEvent(`[Table] searched.`, {
                query,
              });
              setQuery(e.target.value);
              if (pageIndex !== 0) setPageIndex(0);
            }}
            className="flex w-full items-center rounded-lg border border-transparent bg-gray-7 bg-[url('/icons/search.svg')] bg-no-repeat py-3 px-2 pl-8 text-gray-1 outline-none transition duration-150 text-subtitle-1 ![background-position:12px_17px] focus:border-black"
            placeholder={t('table_placeholder')}
          />
        </div>
      </div>

      <div id="topOfTable" className="flex scroll-mt-30 flex-col justify-center">
        <table>
          {!(data && data?.result.data.length <= 0) && <TableHead />}

          {/* If Loaded And Has Results */}
          {data && data?.result.data.length >= 0 && (
            <tbody className='before:block before:h-3 after:block after:h-3 after:[content:"_"_!important]'>
              {data.result.data
                .filter((asset) => {
                  if (activeTab === AssetListTabs.TRADEABLE) {
                    if (!asset.tradingDisabled) {
                      return true;
                    }

                    return false;
                  }

                  return true;
                })
                .map((instrument) => (
                  <TableRow
                    url={`/assets/${instrument.code}`}
                    key={instrument.code}
                    instrument={instrument}
                  />
                ))}
            </tbody>
          )}

          {/* If Not Loaded */}
          {!data && (
            <tbody className='before:block before:h-3 after:block after:h-3 after:[content:"_"_!important]'>
              {Array.from(Array(20).keys()).map((i) => (
                <TableRow key={i} />
              ))}
            </tbody>
          )}
        </table>
        {/* If Loaded But No Results */}
        {data && data?.result.data.length <= 0 && (
          <div className="flex h-95 w-full items-center justify-center p-5">
            <span>{t('no_data', { query })}</span>
          </div>
        )}

        {!data && (
          <nav className="mt-8 mb-12 flex flex-col items-center gap-3 border-t border-gray-6 pt-6 tablet:mb-15">
            <ol className="flex justify-center gap-1 text-body-1">
              {[...Array(6).keys()].slice(1).map((i) => (
                <li
                  key={i}
                  className="flex h-6 w-6 animate-pulse items-center justify-center rounded-full bg-gray-7"
                />
              ))}
            </ol>
            <div className="h-5 w-[170px] animate-pulse rounded bg-gray-7" />
          </nav>
        )}

        {data && data?.result.data.length > 0 && (
          <div className="mb-12 flex w-full flex-col items-center gap-3 tablet:mb-10">
            <ol className="flex w-full justify-center gap-1 border-t border-gray-6 pt-6 text-body-1">
              {pageIndex >= 2 && (
                <li key={0} className={cn('flex h-6 w-6 items-center justify-center rounded-full')}>
                  <button
                    onClick={() => {
                      setPageIndex(0);

                      if (typeof window !== 'undefined')
                        document.getElementById('topOfTable').scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'nearest',
                        });

                      trackUserEvent(`[Table] Changed Page`, {
                        totalItems: data.result.data.length,
                        page: 0,
                        query,
                      });
                    }}
                    className="h-full w-full"
                    type="button"
                  >
                    1
                  </button>
                </li>
              )}
              {pageIndex === 3 && (
                <li key={1} className={cn('flex h-6 w-6 items-center justify-center rounded-full')}>
                  <button
                    onClick={() => {
                      setPageIndex(1);

                      if (typeof window !== 'undefined')
                        document.getElementById('topOfTable').scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                          inline: 'nearest',
                        });

                      trackUserEvent(`[Table] Changed Page`, {
                        totalItems: data.result.data.length,
                        page: 1,
                        query,
                      });
                    }}
                    className="h-full w-full"
                    type="button"
                  >
                    2
                  </button>
                </li>
              )}
              {pageIndex >= 4 && <>&#8230;</>}
              {getPaginationPages().map((page) => (
                <li
                  key={page}
                  className={cn('flex h-6 w-6 items-center justify-center rounded-full', {
                    'bg-gray-6': pageIndex === page,
                  })}
                >
                  {pageIndex !== page ? (
                    <button
                      onClick={() => {
                        setPageIndex(page);
                        if (typeof window !== 'undefined')
                          document.getElementById('topOfTable').scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                          });

                        trackUserEvent(`[Table] Changed Page`, {
                          totalItems: data.result.data.length,
                          page,
                          query,
                        });
                      }}
                      className="h-full w-full"
                      type="button"
                    >
                      {(page + 1).toString()}
                    </button>
                  ) : (
                    <span className="cursor-default">{(page + 1).toString()}</span>
                  )}
                </li>
              ))}
              {pageIndex !== pageMax && (
                <>
                  {pageIndex !== pageMax - 1 && <>&#8230;</>}
                  <li
                    key={pageMax}
                    className={cn('flex h-6 w-6 items-center justify-center rounded-full', {
                      'bg-gray-6': pageIndex === pageMax,
                    })}
                  >
                    <button
                      onClick={() => {
                        setPageIndex(pageMax);

                        if (typeof window !== 'undefined')
                          document.getElementById('topOfTable').scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                            inline: 'nearest',
                          });

                        trackUserEvent(`[Table] Changed Page`, {
                          totalItems: data.result.data.length,
                          page: pageMax,
                          query,
                        });
                      }}
                      className="h-full w-full"
                      type="button"
                    >
                      {(pageMax + 1).toString()}
                    </button>
                  </li>
                </>
              )}
            </ol>
            {Math.ceil(data.result.totalNumber / data.result.pageSize) > 1 && (
              <p className="text-center text-gray-3 tracking-tight">
                {t('pagination', {
                  start: (data.result.page + 1) * data.result.pageSize - (data.result.pageSize - 1),
                  end:
                    (data.result.page + 1) * data.result.pageSize -
                    (data.result.pageSize - 1) +
                    data.result.data.length -
                    1,
                  total: data.result.totalNumber,
                })}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetListTable;
